import React, { useState, useEffect, useCallback } from 'react';
import { getKassenbuch, createKassenbuchEntry, exportKassenbuch } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { BookOpen, Plus, Download, Loader2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const ENTRY_TYPES = [
  { value: 'eigenverbrauch', label: 'Eigenverbrauch', color: 'bg-amber-100 text-amber-800' },
  { value: 'bruch',          label: 'Bruch / Verderb', color: 'bg-red-100 text-red-800' },
  { value: 'einladung',      label: 'Einladung',       color: 'bg-blue-100 text-blue-800' },
  { value: 'einlage',        label: 'Einlage',         color: 'bg-emerald-100 text-emerald-800' },
  { value: 'entnahme',       label: 'Entnahme',        color: 'bg-slate-100 text-slate-800' },
];

function entryTypeBadge(type) {
  const t = ENTRY_TYPES.find(x => x.value === type);
  if (!t) return <Badge variant="secondary">{type}</Badge>;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${t.color}`}>
      {t.label}
    </span>
  );
}

function groupByDate(entries) {
  const groups = {};
  for (const e of entries) {
    const d = e.date || '—';
    if (!groups[d]) groups[d] = [];
    groups[d].push(e);
  }
  return Object.entries(groups).sort((a, b) => b[0].localeCompare(a[0]));
}

export default function AdminKassenbuch() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [form, setForm] = useState({
    entry_type: 'eigenverbrauch',
    amount: '',
    note: '',
    date: new Date().toISOString().slice(0, 10),
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo)   params.date_to   = dateTo;
      const data = await getKassenbuch(params);
      setEntries(data);
    } catch {
      toast.error('Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    if (!form.amount || isNaN(parseFloat(form.amount))) {
      toast.error('Bitte gültigen Betrag eingeben');
      return;
    }
    setSaving(true);
    try {
      await createKassenbuchEntry({
        entry_type: form.entry_type,
        amount: parseFloat(form.amount),
        note: form.note,
        date: form.date || undefined,
      });
      toast.success('Eintrag erstellt');
      setShowDialog(false);
      setForm({ entry_type: 'eigenverbrauch', amount: '', note: '', date: new Date().toISOString().slice(0, 10) });
      load();
    } catch {
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo)   params.date_to   = dateTo;
      const blob = await exportKassenbuch(params);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `kassenbuch_${dateFrom || 'alle'}_${dateTo || 'alle'}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Export fehlgeschlagen');
    } finally {
      setExporting(false);
    }
  };

  const grouped = groupByDate(entries);
  const totalAmount = entries.reduce((s, e) => {
    const sign = ['einlage'].includes(e.entry_type) ? 1 :
                 ['entnahme', 'eigenverbrauch', 'bruch', 'einladung'].includes(e.entry_type) ? -1 : 0;
    return s + sign * (e.amount || 0);
  }, 0);

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#1a2f38]">Kassenbuch</h1>
          <p className="text-slate-500">{entries.length} Einträge</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Input
            type="date"
            value={dateFrom}
            onChange={e => setDateFrom(e.target.value)}
            className="w-36 text-sm"
          />
          <span className="text-slate-400 text-sm">—</span>
          <Input
            type="date"
            value={dateTo}
            onChange={e => setDateTo(e.target.value)}
            className="w-36 text-sm"
          />
          <Button variant="outline" onClick={load} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
          </Button>
          <Button variant="outline" onClick={handleExport} disabled={exporting}>
            {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
            CSV
          </Button>
          <Button className="bg-[#1a2f38] hover:bg-[#2a4048]" onClick={() => setShowDialog(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Neuer Eintrag
          </Button>
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {ENTRY_TYPES.map(t => {
          const sum = entries.filter(e => e.entry_type === t.value).reduce((s, e) => s + (e.amount || 0), 0);
          return (
            <Card key={t.value} className="shadow-sm">
              <CardContent className="pt-4 pb-3">
                <p className="text-xs text-slate-500 mb-1">{t.label}</p>
                <p className="text-lg font-bold text-[#1a2f38]">€ {sum.toFixed(2)}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Entries grouped by date */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-[#1a2f38]" />
        </div>
      ) : grouped.length === 0 ? (
        <Card className="shadow-sm">
          <CardContent className="py-12 text-center">
            <BookOpen className="w-12 h-12 mx-auto mb-4 text-slate-300" />
            <p className="text-slate-500">Noch keine Kassenbuch-Einträge</p>
          </CardContent>
        </Card>
      ) : (
        grouped.map(([date, dayEntries]) => {
          const dayTotal = dayEntries.reduce((s, e) => {
            const sign = ['einlage'].includes(e.entry_type) ? 1 : -1;
            return s + sign * (e.amount || 0);
          }, 0);
          return (
            <Card key={date} className="shadow-sm">
              <CardHeader className="py-3 px-4 border-b bg-slate-50">
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-sm text-[#1a2f38]">{date}</span>
                  <span className={`text-sm font-medium ${dayTotal >= 0 ? 'text-emerald-700' : 'text-slate-700'}`}>
                    {dayTotal >= 0 ? '+' : ''}€ {dayTotal.toFixed(2)}
                  </span>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <table className="w-full text-sm">
                  <tbody>
                    {dayEntries.map(e => (
                      <tr key={e.id} className="border-b last:border-0 hover:bg-slate-50">
                        <td className="px-4 py-2.5">{entryTypeBadge(e.entry_type)}</td>
                        <td className="px-4 py-2.5 text-right font-medium">€ {(e.amount || 0).toFixed(2)}</td>
                        <td className="px-4 py-2.5 text-slate-500 hidden md:table-cell">{e.note || '—'}</td>
                        <td className="px-4 py-2.5 text-slate-400 text-xs text-right">{e.user_name || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          );
        })
      )}

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Neuer Kassenbuch-Eintrag</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div>
              <Label>Datum</Label>
              <Input
                type="date"
                value={form.date}
                onChange={e => setForm({ ...form, date: e.target.value })}
              />
            </div>
            <div>
              <Label>Typ</Label>
              <div className="grid grid-cols-2 gap-2 mt-1">
                {ENTRY_TYPES.map(t => (
                  <button
                    key={t.value}
                    type="button"
                    onClick={() => setForm({ ...form, entry_type: t.value })}
                    className={`py-2 rounded-lg border text-sm transition-colors ${
                      form.entry_type === t.value
                        ? 'bg-[#1a2f38] text-white border-[#1a2f38]'
                        : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label>Betrag (€) *</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={form.amount}
                onChange={e => setForm({ ...form, amount: e.target.value })}
                placeholder="0.00"
                autoFocus
              />
            </div>
            <div>
              <Label>Notiz</Label>
              <Input
                value={form.note}
                onChange={e => setForm({ ...form, note: e.target.value })}
                placeholder="Optional"
              />
            </div>
            <Button onClick={handleCreate} disabled={saving} className="w-full bg-[#1a2f38]">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Eintrag erstellen
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
